// Inspecteur → Risico register (FRONTEND-BLUEPRINT §3.1 / §4). Re-skin of the risk register on the shadcn
// design system: PageHeader + DataTable, reusing api.listRisico() (no new endpoints). A risk =
// Verwerking × Bedreiging × Bedrijfsmiddel with inherent/residual likelihood×impact + a treatment decision.
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import type { Column } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import {
  RegisterScreen, EditDialog, DeactivateButton, type Row, type FormField,
} from "./_shared";

const SCALES = ["very_low", "low", "medium", "high", "very_high"];
const TREATMENTS = ["avoid", "reduce", "transfer", "accept"];
const scaleOpts = SCALES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }));

function ScoreBadge({ likelihood, impact }: { likelihood: unknown; impact: unknown }) {
  if (likelihood == null) return <span className="text-muted-foreground">—</span>;
  return <Badge variant="outline" className="font-mono">{String(likelihood)} × {String(impact ?? "?")}</Badge>;
}

const EDIT_FIELDS: FormField[] = [
  { key: "inherentLikelihood", label: "Inherent likelihood", type: "select", required: true, options: scaleOpts },
  { key: "inherentImpact", label: "Inherent impact", type: "select", required: true, options: scaleOpts },
  { key: "residualLikelihood", label: "Residual likelihood", type: "select", options: scaleOpts },
  { key: "residualImpact", label: "Residual impact", type: "select", options: scaleOpts },
  { key: "treatmentDecision", label: "Treatment decision", type: "select", options: TREATMENTS.map((s) => ({ value: s, label: s })) },
  { key: "description", label: "Description", type: "textarea" },
];

export function InspectorRisicoRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["risico"] });
  const [editing, setEditing] = useState<string | null>(null);

  const columns: Column<Row>[] = [
    { key: "verwerking", header: "Verwerking", render: (r) => <span className="font-medium">{String(r.verwerking ?? "—")}</span> },
    { key: "bedreiging", header: "Bedreiging", render: (r) => String(r.bedreiging ?? "—") },
    { key: "bedrijfsmiddel", header: "Bedrijfsmiddel", render: (r) => String(r.bedrijfsmiddel ?? "—") },
    { key: "inherent", header: "Inherent (L×I)", render: (r) => <ScoreBadge likelihood={r.inherentLikelihood} impact={r.inherentImpact} /> },
    { key: "residual", header: "Residueel (L×I)", render: (r) => (r.residualLikelihood ? <ScoreBadge likelihood={r.residualLikelihood} impact={r.residualImpact} /> : <span className="text-muted-foreground">—</span>) },
    { key: "treatmentDecision", header: "Behandeling", render: (r) => (r.treatmentDecision ? <Badge variant="secondary" className="capitalize">{String(r.treatmentDecision)}</Badge> : <span className="text-muted-foreground">—</span>) },
  ];

  return (
    <>
      <RegisterScreen
        title={t("page.risico")}
        description={t("page.risico.sub")}
        queryKey="risico"
        listFn={() => api.listRisico()}
        columns={columns}
        empty={t("inspector.risico.empty")}
        readPerm="risico.read"
        onRowClick={(r) => { if (r.id) void navigate({ to: "/risico/$id", params: { id: String(r.id) } }); }}
        rowActions={(r) => {
          const id = String(r.id);
          return (
            <span className="inline-flex items-center gap-1">
              <Can perm="risico.update"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setEditing(id)}>{t("crud.edit")}</button></Can>
              <Can perm="risico.delete"><DeactivateButton basePath={TENANT_PATHS.risico} id={id} onDone={invalidate} /></Can>
            </span>
          );
        }}
      />
      {editing && (
        <EditDialog
          basePath={TENANT_PATHS.risico}
          id={editing}
          title={t("inspector.risico.edit")}
          fields={EDIT_FIELDS}
          onClose={() => setEditing(null)}
          onDone={() => { invalidate(); setEditing(null); }}
        />
      )}
    </>
  );
}
